import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const costCentersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cost-centers',
  component: CostCentersPage,
});

function CostCentersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [isDefaultOverhead, setIsDefaultOverhead] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['cost-centers'],
    queryFn: () => api.listCostCenters(),
  });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  const isAdmin = meQuery.data?.person.roles.includes('Admin') ?? false;

  const createMutation = useMutation({
    mutationFn: () => api.createCostCenter({ name, isDefaultOverhead }),
    onSuccess: async () => {
      setDialogOpen(false);
      setName('');
      setIsDefaultOverhead(false);
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['cost-centers'] });
    },
    onError: (err) => {
      setErrorMessage(toMessage(err));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.deactivateCostCenter(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cost-centers'] });
    },
  });

  const [renaming, setRenaming] = React.useState<{ id: string; name: string } | null>(null);
  const [renameTo, setRenameTo] = React.useState('');
  const [renameError, setRenameError] = React.useState<string | null>(null);

  const openRename = (row: { id: string; name: string }): void => {
    setRenaming(row);
    setRenameTo(row.name);
    setRenameError(null);
  };

  const renameMutation = useMutation({
    mutationFn: (args: { id: string; name: string }) =>
      api.renameCostCenter(args.id, { name: args.name }),
    onSuccess: async () => {
      setRenaming(null);
      await queryClient.invalidateQueries({ queryKey: ['cost-centers'] });
    },
    onError: (err) => {
      setRenameError(toMessage(err));
    },
  });

  const trimmedRename = renameTo.trim();
  const renameUnchanged = renaming !== null && trimmedRename === renaming.name;

  const rows = listQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Cost centers
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            The Overhead sink cannot be deactivated. Every project must resolve to a cost center.
            {isAdmin ? '' : ' Adding, renaming or deactivating one is an Admin action.'}
          </p>
        </div>
        {isAdmin ? (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>Add cost center</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New cost center</DialogTitle>
              <DialogDescription>
                Marking as default overhead is only allowed if none exists yet.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.currentTarget.value)} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isDefaultOverhead}
                  onChange={(e) => setIsDefaultOverhead(e.currentTarget.checked)}
                />
                Mark as default overhead sink
              </label>
              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}
              <DialogFooter>
                <Button
                  onClick={() => createMutation.mutate()}
                  disabled={createMutation.isPending || !name}
                >
                  {createMutation.isPending ? 'Saving…' : 'Create'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All cost centers</CardTitle>
          <CardDescription>{rows.length} total</CardDescription>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">None yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {rows.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium">{c.name}</div>
                    <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                      {c.isDefaultOverhead ? <Badge variant="default">overhead</Badge> : null}
                      <span>{c.active ? 'active' : 'inactive'}</span>
                    </div>
                  </div>
                  {isAdmin ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={!c.active}
                        onClick={() => openRename({ id: c.id, name: c.name })}
                      >
                        Rename
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={
                          c.isDefaultOverhead || !c.active || deactivateMutation.isPending
                        }
                        onClick={() => deactivateMutation.mutate(c.id)}
                      >
                        {c.isDefaultOverhead ? 'Protected' : c.active ? 'Deactivate' : 'Inactive'}
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={renaming !== null} onOpenChange={(open) => !open && setRenaming(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename cost center</DialogTitle>
            <DialogDescription>
              Existing projects and transactions keep pointing at this cost center under its new
              name.
            </DialogDescription>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (renaming && trimmedRename && !renameUnchanged) {
                renameMutation.mutate({ id: renaming.id, name: trimmedRename });
              }
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cost-center-rename">Name</Label>
              <Input
                id="cost-center-rename"
                autoFocus
                value={renameTo}
                onChange={(e) => setRenameTo(e.currentTarget.value)}
              />
            </div>
            {renameError ? (
              <Alert variant="destructive">
                <AlertTitle>Failed</AlertTitle>
                <AlertDescription>{renameError}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenaming(null)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={renameMutation.isPending || !trimmedRename || renameUnchanged}
              >
                {renameMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
